import type User from '~~/app/auth/models/User';

interface UserAvatarDisplay {
    imageUrl: string | null;
    initials: string;
    backgroundColor: string;
    textColor: string;
}

/**
 * Neutral grey for users who never picked a colour. Deliberately not a branding colour: the whole
 * point is that a user without a colour must not look like they share one with everybody else.
 */
const FALLBACK_BACKGROUND = '#a3a3a3';

const LIGHT_TEXT = '#ffffff';
const DARK_TEXT = '#1f2937';

/**
 * Lightness band of the Laravolt-generated placeholder these pastilles replace (measured across its
 * 16 configured colours: 74-87%, averaging 81%). `useColors()` at full strength averages 44%, which
 * is why the pastilles read as heavy next to what users had before.
 *
 * Only lightness is remapped; hue and saturation stay the user's. Remapping rather than forcing a
 * fixed lightness (`hex2pastel`) is deliberate: a fixed value merges #757575, #313131 and #000000
 * into one grey, putting three differently-configured users back on an identical pastille.
 */
const PLACEHOLDER_LIGHTNESS_FLOOR = 74;
const PLACEHOLDER_LIGHTNESS_CEILING = 87;
const LIGHTNESS_COMPRESSION = 0.2;

const parseHex = (color: string): [number, number, number] | null => {
    const hex = color.trim().replace(/^#/, '');

    const expanded = hex.length === 3 ? [...hex].map((character) => character + character).join('') : hex;

    if (!/^[0-9a-f]{6}$/i.test(expanded)) {
        return null;
    }

    return [parseInt(expanded.slice(0, 2), 16), parseInt(expanded.slice(2, 4), 16), parseInt(expanded.slice(4, 6), 16)];
};

/**
 * WCAG relative luminance. Used to pick the readable text colour rather than hardcoding white:
 * the palette in `useColors()` runs from #ffc40e to #000000, so white fails on the light end.
 */
const relativeLuminance = ([red, green, blue]: [number, number, number]): number => {
    const [r, g, b] = [red, green, blue].map((channel) => {
        const ratio = channel / 255;
        return ratio <= 0.03928 ? ratio / 12.92 : Math.pow((ratio + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrastRatio = (a: number, b: number): number => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

const toHex = (rgb: [number, number, number]): string => `#${rgb.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;

/** Lifts a colour into the placeholder lightness band, preserving its hue and saturation. */
const liftIntoPlaceholderBand = ([red, green, blue]: [number, number, number]): [number, number, number] => {
    const [r, g, b] = [red / 255, green / 255, blue / 255];
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    const lightness = (max + min) / 2;
    const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));

    let hue = 0;
    if (delta !== 0) {
        if (max === r) {
            hue = (((g - b) / delta) % 6) * 60;
        } else if (max === g) {
            hue = ((b - r) / delta + 2) * 60;
        } else {
            hue = ((r - g) / delta + 4) * 60;
        }
    }
    if (hue < 0) {
        hue += 360;
    }

    const lifted = Math.min(PLACEHOLDER_LIGHTNESS_CEILING, PLACEHOLDER_LIGHTNESS_FLOOR + lightness * 100 * LIGHTNESS_COMPRESSION) / 100;
    const chroma = (1 - Math.abs(2 * lifted - 1)) * saturation;
    const secondary = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
    const offset = lifted - chroma / 2;

    const sector = Math.floor(hue / 60) % 6;
    const sectors: Array<[number, number, number]> = [
        [chroma, secondary, 0],
        [secondary, chroma, 0],
        [0, chroma, secondary],
        [0, secondary, chroma],
        [secondary, 0, chroma],
        [chroma, 0, secondary],
    ];

    return sectors[sector].map((channel) => Math.round((channel + offset) * 255)) as [number, number, number];
};

const readableTextColor = (rgb: [number, number, number]): string => {
    const luminance = relativeLuminance(rgb);
    const againstLight = contrastRatio(luminance, relativeLuminance([255, 255, 255]));
    const againstDark = contrastRatio(luminance, relativeLuminance([31, 41, 55]));

    return againstLight >= againstDark ? LIGHT_TEXT : DARK_TEXT;
};

const initialsOf = (name: string): string => {
    const words = name.trim().split(/\s+/).filter(Boolean);

    if (!words.length) {
        return '';
    }

    if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase();
    }

    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/**
 * Resolves how a user's avatar should be drawn. `imageUrl` is a candidate, not a guarantee: it may
 * be empty, or point at an image the API no longer serves, so callers must fall back to the
 * initials on a load error rather than trusting its presence.
 */
export default function useUserAvatarDisplay(user: User | null): UserAvatarDisplay {
    const chosen = parseHex(user?.color ? String(user.color) : '') ?? parseHex(FALLBACK_BACKGROUND)!;
    const background = liftIntoPlaceholderBand(chosen);

    return {
        imageUrl: user?.avatar || null,
        initials: initialsOf(user?.name ?? ''),
        backgroundColor: toHex(background),
        textColor: readableTextColor(background),
    };
}
